import React, { FC, useEffect, useState } from 'react'
import moment from 'moment/moment'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip'
import { Label } from '../../ui/label'
import { Checkbox } from '../../ui/checkbox'

import { getDays } from '../../../helpers/registration-on-event'
import { cn } from '../../../lib/utils'

import { IRegistrationFees } from '../AddClasses'
import { IEventReviewPublish } from '../../../models/event-review-publish/event-review-publish.interface'

import { daysOfWeek } from '../../../helpers/time'

interface AddClassItemProps {
  item: IRegistrationFees
  publishedEvent: IEventReviewPublish | null
  onCheckedChange: (id: string, value: boolean, key: string) => void
  className?: string
  activeDay?: string
}

const AddClassItem: FC<AddClassItemProps> = ({
  item,
  publishedEvent,
  onCheckedChange,
  className,
  activeDay,
}) => {
  const isStartDate = daysOfWeek[new Date(item.startDate)?.getDay()] === activeDay
  const isStartDateChildren = item.children?.find(
    (children) => daysOfWeek[new Date(children.startDate)?.getDay()] === activeDay
  )

  const isQualify =
    item.qualifyingClass?.name &&
    item.qualifyingClass?.price &&
    publishedEvent?.EventFees?.qualifyingClasses?.isEnable

  return isStartDate || isStartDateChildren ? (
    <div key={item.name} className={cn('w-full flex gap-2', className)}>
      <Label
        className={cn(
          'relative flex-wrap font-normal cursor-pointer rounded-md border px-4 py-2 flex-1 pr-10 gap-x-2 gap-y-1 min-h-[58px]',
          item.selected ? 'bg-black/[3%] border-transparent' : 'border-black/10'
        )}
      >
        {item.name}
        {item.order ? (
          <span className="text-xs text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rounded-full py-0.5 px-3">
            {item.order}
          </span>
        ) : null}
        {isQualify && (
          <span className="w-full block text-black/50 text-xs">
            {item.qualifyingClass?.name || 'No extra fee'}
          </span>
        )}
        <Checkbox
          checked={item.selected || false}
          className="absolute z-10 right-3 top-1/2 -translate-y-1/2 rounded-full w-5 h-5 border-black/20"
          onCheckedChange={(value) => onCheckedChange(item.uuid, value as boolean, 'selected')}
        />
      </Label>
      <Label
        className={cn(
          ' flex items-center justify-center w-14 rounded-md border',
          item.isQualify ? 'bg-black/[3%] border-transparent' : 'border-black/10',
          isQualify ? 'cursor-pointer' : 'bg-black/[2%] cursor-not-allowed border-transparent'
        )}
      >
        <Checkbox
          disabled={!isQualify}
          checked={item.isQualify || false}
          className=" rounded-full w-5 h-5 border-black/20"
          onCheckedChange={(value) => onCheckedChange(item.uuid, value as boolean, 'isQualify')}
        />
      </Label>
    </div>
  ) : null
}

interface SelectClassesProps {
  publishedEvent: IEventReviewPublish | null
  classes: IRegistrationFees[]
  setClasses: (value: IRegistrationFees[]) => void
  setSelectedClasses: (value: IRegistrationFees[]) => void
}

export const SelectClasses: FC<SelectClassesProps> = ({
  publishedEvent,
  classes,
  setClasses,
  setSelectedClasses,
}) => {
  const [filteredClasses, setFilteredClasses] = useState<IRegistrationFees[]>([])
  const [activeDay, setActiveDay] = useState<string>()
  const [days, setDays] = useState<{ day: string; date: string }[]>([])

  useEffect(() => {
    const { allDates } = getDays(classes)
    setDays(allDates)
    if (!activeDay?.[0]) setActiveDay(allDates[0]?.day)
  }, [activeDay, classes])

  useEffect(() => {
    setFilteredClasses(classes)
  }, [classes, activeDay])

  const onCheckedChange = (id: string, value: boolean, key: string) => {
    const classesUpdated = classes.map((item) =>
      item.uuid === id
        ? {
            ...item,
            [key]: value || false,
            children:
              item.children?.map((children) => ({ ...children, [key]: value || false })) ||
              item.children,
          }
        : item.children
          ? {
              ...item,
              children: item.children.map((children) =>
                children.uuid === id ? { ...children, [key]: value || false } : children
              ),
            }
          : item
    )

    const newSelected: IRegistrationFees[] = []

    classesUpdated.forEach((item: IRegistrationFees) => {
      if (item.selected) {
        newSelected.push(item)
      }
      item.children?.forEach((children) => {
        if (children.selected) {
          newSelected.push(children)
        }
      })
    })

    setClasses([...classesUpdated])
    setSelectedClasses([...newSelected])
  }

  return (
    <>
      <div className="flex gap-2 justify-between items-center mb-1">
        <h4 className="text-lg font-normal">Select classes</h4>
        <Select value={activeDay} onValueChange={(value) => setActiveDay(value)}>
          <SelectTrigger className="disabled:bg-input p-0 border-none underline">
            <SelectValue placeholder="Select day..." />
          </SelectTrigger>
          <SelectContent>
            {days?.map((day) => (
              <SelectItem key={day.day} value={day.day}>
                {day.day}, {moment(day.date).format('MMM DD')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="relative ml-auto mb-1">
        <Tooltip>
          <TooltipTrigger className="text-right text-xs text-black/50 pr-2 underline">
            Bonus?
          </TooltipTrigger>
          <TooltipContent>Do you want to pay the extra class fee?</TooltipContent>
        </Tooltip>
      </div>
      <div className="flex flex-col gap-3">
        {filteredClasses.map((item) => (
          <React.Fragment key={item.uuid}>
            <AddClassItem
              item={item}
              onCheckedChange={onCheckedChange}
              publishedEvent={publishedEvent}
              activeDay={activeDay}
            />
            {item.children?.map((children) => (
              <AddClassItem
                key={children.uuid}
                className="pl-6"
                item={children}
                onCheckedChange={onCheckedChange}
                publishedEvent={publishedEvent}
                activeDay={activeDay}
              />
            ))}
          </React.Fragment>
        ))}
      </div>
    </>
  )
}
